import type { PortalCatalogFilterFieldDto } from '@supply-mover-2-0/shared';

/**
 * URL ⇄ faceted-filter mapping for the catalog page.
 *
 * Facet state lives in the URL (single source of truth, same as every other
 * catalog filter) under a `f.` namespace so it can never collide with the
 * legacy params:
 *   terms   → repeated values:  f.manufacturer=Pfizer&f.manufacturer=Bayer
 *   range   → f.price.min=5&f.price.max=20
 *   date    → f.soonestExpirationDate.from=2026-01-01&f...to=2026-02-01
 *   boolean → f.isHazmat=true
 *
 * DYNAMIC toggles (isInStock / isOnPromo / isOnOrder) intentionally keep the
 * LEGACY param names (?isInStock=true) — existing deep links and the header's
 * URL-building keep working, and the rail simply reads/writes those params.
 * category/subcategory also keep their legacy params (the category tree owns
 * them); when the site ALSO exposes them as rail facets the values merge.
 */

const FACET_PARAM_PREFIX = 'f.';

/** Legacy boolean params that predate the faceted rail (dynamic + per-customer toggles). */
const LEGACY_TOGGLE_PARAMS = [
	'isInStock',
	'isShortDate',
	'isFavorite',
	'isPreviouslyPurchased',
	'isOnOrder',
	'isOnPromo',
] as const;

/** The param a dynamic toggle reads/writes: its own key (legacy name) — e.g. isInStock. */
function dynamicParamName(fieldKey: string): string {
	return fieldKey;
}

/**
 * Build the faceted request's `filters` object from the URL for the given
 * exposed fields. Only exposed fields produce entries (the server would drop
 * anything else anyway). `category`/`subcategory` legacy params are merged
 * into their terms filters so the category tree and the rail compose.
 */
export function parseFacetFilters(
	searchParams: URLSearchParams,
	filterFields: Array<PortalCatalogFilterFieldDto>,
	options: {
		selectedCategory: string;
		selectedSubcategory: string;
		isAuthenticated: boolean;
		hasActiveClient: boolean;
	},
): Record<string, unknown> {
	const filters: Record<string, unknown> = {};

	// Category tree selection → terms filters, regardless of rail exposure.
	// The server ALWAYS accepts these two keys (CATEGORY_NAV_FILTER_KEYS on
	// the API side) — the Categories navigation section owns category
	// browsing, so tree clicks must filter even when the site doesn't expose
	// category/subcategory as rail facets. When the site DOES expose them,
	// the values merge with any rail selections below.
	if (options.selectedCategory.length > 0) {
		filters.category = [options.selectedCategory];
	}

	if (options.selectedSubcategory.length > 0) {
		filters.subcategory = [options.selectedSubcategory];
	}

	for (const field of filterFields) {
		if (field.resolution === 'dynamic') {
			if (searchParams.get(dynamicParamName(field.key)) === 'true') {
				filters[field.key] = true;
			}

			continue;
		}

		switch (field.kind) {
			case 'terms': {
				const values = searchParams.getAll(`${FACET_PARAM_PREFIX}${field.key}`);

				if (values.length > 0) {
					const existing = Array.isArray(filters[field.key])
						? (filters[field.key] as Array<string>)
						: [];
					filters[field.key] = [...new Set([...existing, ...values])];
				}

				break;
			}
			case 'range': {
				const min = searchParams.get(`${FACET_PARAM_PREFIX}${field.key}.min`);
				const max = searchParams.get(`${FACET_PARAM_PREFIX}${field.key}.max`);
				const minNum = min != null ? Number(min) : Number.NaN;
				const maxNum = max != null ? Number(max) : Number.NaN;
				const range: { min?: number; max?: number } = {};

				if (Number.isFinite(minNum)) {
					range.min = minNum;
				}

				if (Number.isFinite(maxNum)) {
					range.max = maxNum;
				}

				if (range.min != null || range.max != null) {
					filters[field.key] = range;
				}

				break;
			}
			case 'date': {
				const from = searchParams.get(`${FACET_PARAM_PREFIX}${field.key}.from`);
				const to = searchParams.get(`${FACET_PARAM_PREFIX}${field.key}.to`);
				const range: { from?: string; to?: string } = {};

				if (from != null && from.length > 0) {
					range.from = from;
				}

				if (to != null && to.length > 0) {
					range.to = to;
				}

				if (range.from != null || range.to != null) {
					filters[field.key] = range;
				}

				break;
			}
			case 'boolean': {
				// Also accept the bare legacy param (?isShortDate=true) so
				// pre-rail deep links keep filtering.
				if (
					searchParams.get(`${FACET_PARAM_PREFIX}${field.key}`) === 'true' ||
					searchParams.get(field.key) === 'true'
				) {
					filters[field.key] = true;
				}

				break;
			}
			default:
				break;
		}
	}

	// Per-customer toggles (reserved keys, not rail fields) — authenticated only;
	// previously-purchased additionally needs an active client (mirrors the
	// legacy toggle gating).
	if (options.isAuthenticated && searchParams.get('isFavorite') === 'true') {
		filters.isFavorite = true;
	}

	if (
		options.isAuthenticated &&
		options.hasActiveClient &&
		searchParams.get('isPreviouslyPurchased') === 'true'
	) {
		filters.isPreviouslyPurchased = true;
	}

	return filters;
}

/**
 * Compute the URL-param updates that clear EVERY facet + legacy filter param.
 * Returned as a map of param → null for the page's updateSearchParams helper
 * (repeated `f.` params are enumerated from the current URL).
 */
export function buildClearAllFacetParams(
	searchParams: URLSearchParams,
): Record<string, null> {
	const updates: Record<string, null> = {};

	for (const key of searchParams.keys()) {
		if (key.startsWith(FACET_PARAM_PREFIX)) {
			updates[key] = null;
		}
	}

	for (const legacy of LEGACY_TOGGLE_PARAMS) {
		updates[legacy] = null;
	}

	return updates;
}

/** True when any rail facet or legacy toggle is active in the URL. */
export function hasActiveFacetFilters(searchParams: URLSearchParams): boolean {
	for (const key of searchParams.keys()) {
		if (key.startsWith(FACET_PARAM_PREFIX)) {
			return true;
		}
	}

	return LEGACY_TOGGLE_PARAMS.some(
		(legacy) => searchParams.get(legacy) === 'true',
	);
}

/** The URL param name a rail control reads/writes for a field (per kind + suffix). */
export function facetParamName(
	field: Pick<PortalCatalogFilterFieldDto, 'key' | 'resolution'>,
	suffix?: 'min' | 'max' | 'from' | 'to',
): string {
	if (field.resolution === 'dynamic') {
		return dynamicParamName(field.key);
	}

	const base = `${FACET_PARAM_PREFIX}${field.key}`;

	return suffix != null ? `${base}.${suffix}` : base;
}
